import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@tcc-online/common-components';
import { useTranslate } from 'react-translate.ts';
import styles from './RegistrationPage.module.scss';
import {
    NOT_FOUND_PATH,
    SIGN_IN_TOKEN_PATH,
} from '../../../constants/route.constants';
import RegistrationForm from './RegistrationForm';
import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { TRANSLATIONS_REGISTER_KEY } from '../../../constants/translation.constants';
import { type AppDispatch } from '../../../reducers/store';
import { css, cx } from '@emotion/css';
import { useTheme } from '@tcc-online/react-theme-provider';
import AuthLayout from '../AuthLayout/AuthLayout';
import AuthCard from '../../../components/auth/AuthCard/AuthCard';
import LanguageSlice from '../../../common/slices/LanguageSlice';
import RegistrationSlice from '../../../common/slices/RegistrationSlice';
import { StorageService } from '../../../services';
import { REGISTRATION_DATA_KEY } from '../../../services/common/RegistrationService';
import { Base64 } from 'js-base64';

export default function RegistrationPage() {
    const { loading, sending } = useSelector(RegistrationSlice.state.getAll);
    const translate = useTranslate(TRANSLATIONS_REGISTER_KEY);
    const [theme] = useTheme();
    return (
        <AuthLayout className={styles.page}>
            <AuthCard
                isLoading={loading || sending}
                header={
                    <div
                        className={cx(
                            styles.title,
                            css({ color: theme.primaryColor }),
                        )}
                    >
                        {translate('title')}
                    </div>
                }
            >
                <Content />
            </AuthCard>
        </AuthLayout>
    );
}

function Content() {
    const dispatch = useDispatch<AppDispatch>();
    const { id, param } = useParams<{ id: string; param?: string }>();
    const navigate = useNavigate();
    const locale = useSelector(LanguageSlice.state.getLocale);
    const params = useRef<string>(null) as MutableRefObject<string>;

    useEffect(() => {
        if (id) {
            dispatch(RegistrationSlice.actions.getRegistration(id));
        }
    }, [id, dispatch, locale]);

    const registration = useSelector(RegistrationSlice.state.getAll);
    const { data, error, loading } = registration;
    const localRegistrationData = useMemo(
        (): { email: string; urlToken: string } | undefined =>
            StorageService.getLocal(REGISTRATION_DATA_KEY + id),
        [id],
    );
    const email = localRegistrationData?.email || registration.email;
    const urlToken = localRegistrationData?.urlToken || registration.urlToken;

    useEffect(() => {
        if (error && !loading) {
            navigate(NOT_FOUND_PATH, { replace: true, relative: 'path' });
        }
    }, [error, loading]);

    useEffect(() => {
        if (param) {
            params.current = JSON.parse(Base64.decode(param));
        }
    }, [param]);

    if (email) {
        return <Email email={email} />;
    }

    if (urlToken) {
        return <Token urlToken={urlToken} />;
    }

    if (!data) {
        return null;
    }

    return <RegistrationForm {...data} params={params.current} />;
}

function Email(props: { email: string }) {
    const translate = useTranslate(TRANSLATIONS_REGISTER_KEY + '.result.email');

    const { email } = props;

    return (
        <>
            <p
                dangerouslySetInnerHTML={{
                    __html: translate('p1', {
                        0: `<strong>${email}</strong>`,
                    }),
                }}
            />
            <br />
            <p
                dangerouslySetInnerHTML={{
                    __html: translate('p2', {
                        0: '<a href="mailto:info@tcconline.cz"><strong>info@tcconline.cz</strong></a>',
                    }),
                }}
            />
        </>
    );
}

function Token(props: { urlToken: string }) {
    const translate = useTranslate(
        TRANSLATIONS_REGISTER_KEY + '.result.anonymous',
    );

    const { urlToken } = props;

    const link = generatePath(SIGN_IN_TOKEN_PATH, {
        token: urlToken,
        params: null,
    });
    return (
        <>
            <p>{translate('p1')}</p>
            <br />
            <p>
                <code>{window.location.origin + link}</code>
            </p>
            <br />
            <p>{translate('p2')}</p>
            <br />
            <p>{translate('p3')}</p>
            <br />
            <Link to={link} relative="path">
                <Button variant={ButtonVariants.positive} isPill>
                    {translate('login')}
                </Button>
            </Link>
        </>
    );
}
